import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { LogOut, Users, CalendarDays, Globe2, MapPin, Loader2, ShieldAlert, MousePointerClick, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAdminAuth } from "../context/AdminAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANG_NAMES = {
  en: "English", ru: "Русский", es: "Español", fr: "Français", de: "Deutsch", it: "Italiano",
  pt: "Português", pl: "Polski", tr: "Türkçe", ar: "العربية", fa: "فارسی", hi: "हिन्दी",
  ja: "日本語", ko: "한국어", zh: "中文", az: "Azərbaycan", ro: "Română",
};

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-[#0A0A0C] border border-white/10 rounded-2xl p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-zinc-500">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        <span className="font-mono-data text-xs uppercase">{label}</span>
      </div>
      <p className="mt-3 font-display font-bold text-3xl text-white">{value}</p>
    </div>
  );
}

function RankList({ icon: Icon, title, items, renderLabel, testId }) {
  const max = items.length ? Math.max(...items.map((i) => i.count)) : 1;
  return (
    <div className="bg-[#0A0A0C] border border-white/10 rounded-2xl p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-zinc-500 mb-5">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        <span className="font-mono-data text-xs uppercase">{title}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-600">Нет данных</p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={renderLabel(item)}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-zinc-300">{renderLabel(item)}</span>
                <span className="font-mono-data text-zinc-500">{item.count}</span>
              </div>
              <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#00F0FF] rounded-full"
                  style={{ width: `${(item.count / max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/analytics/stats`, { withCredentials: true })
      .then((res) => setStats(res.data))
      .catch(() => setError("Не удалось загрузить статистику"));
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const [exportPeriod, setExportPeriod] = useState("7");

  const onExportCsv = async () => {
    try {
      const days = exportPeriod === "all" ? "" : `?days=${exportPeriod}`;
      const res = await axios.get(`${API}/analytics/export-clicks-csv${days}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const periodSuffix = exportPeriod === "all" ? "all" : `last${exportPeriod}days`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `google_play_clicks_${periodSuffix}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Не удалось скачать CSV");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard-page">
      <div className="noise-overlay" />
      <header className="border-b border-white/10 px-6 lg:px-10 py-5 flex items-center justify-between">
        <div>
          <h1 className="font-display font-semibold text-lg text-white">Статистика сайта</h1>
          <p className="text-xs text-zinc-500 mt-0.5" data-testid="admin-dashboard-email">{admin?.email}</p>
        </div>
        <Button
          onClick={onLogout}
          variant="outline"
          data-testid="admin-logout-button"
          className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
        >
          <LogOut className="w-4 h-4 mr-2" /> Выйти
        </Button>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {error && <p className="text-sm text-red-400" data-testid="admin-dashboard-error">{error}</p>}

        {!stats && !error && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" />
          </div>
        )}

        {stats && (
          <div className="space-y-8" data-testid="admin-dashboard-content">
            {stats.jwtRotation?.rotation_due && (
              <div
                className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4"
                data-testid="admin-jwt-rotation-banner"
              >
                <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-sm text-amber-200">
                  Пора сменить JWT-секрет (рекомендуется каждые 90 дней).{" "}
                  {stats.jwtRotation.days_since_rotation != null
                    ? `Прошло ${stats.jwtRotation.days_since_rotation} дней с последней ротации.`
                    : "Дата последней ротации не задана."}{" "}
                  Обновите <code className="text-amber-300">JWT_SECRET</code> и{" "}
                  <code className="text-amber-300">JWT_SECRET_ROTATED_AT</code> в backend/.env.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <span className="font-mono-data text-xs text-zinc-500 uppercase">Статистика</span>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <Select value={exportPeriod} onValueChange={setExportPeriod}>
                  <SelectTrigger
                    data-testid="admin-export-period-select"
                    className="w-full sm:w-[160px] border-white/10 bg-transparent text-zinc-300"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0C] border-white/10 text-zinc-300">
                    <SelectItem value="7" data-testid="admin-export-period-7">Последние 7 дней</SelectItem>
                    <SelectItem value="30" data-testid="admin-export-period-30">Последние 30 дней</SelectItem>
                    <SelectItem value="all" data-testid="admin-export-period-all">Всё время</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={onExportCsv}
                  variant="outline"
                  data-testid="admin-export-csv-button"
                  className="w-full sm:w-auto border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
                >
                  <Download className="w-4 h-4 mr-2" /> Скачать CSV (клики Google Play)
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Всего посещений" value={stats.total} testId="admin-stat-total" />
              <StatCard icon={CalendarDays} label="Сегодня" value={stats.today} testId="admin-stat-today" />
              <StatCard
                icon={MousePointerClick}
                label="Клики Google Play"
                value={stats.googlePlayClicksTotal ?? 0}
                testId="admin-stat-google-play-clicks-total"
              />
              <StatCard
                icon={MousePointerClick}
                label="Клики Google Play сегодня"
                value={stats.googlePlayClicksToday ?? 0}
                testId="admin-stat-google-play-clicks-today"
              />
            </div>

            <div className="bg-[#0A0A0C] border border-white/10 rounded-2xl p-6" data-testid="admin-daily-chart">
              <span className="font-mono-data text-xs text-zinc-500 uppercase">Посещения за 30 дней</span>
              <div className="h-64 mt-4 -mx-2">
                <ResponsiveContainer width="100%" height={256} minWidth={0}>
                  <AreaChart data={stats.daily} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="visitsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#1a1a1c" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#71717A", fontSize: 10 }}
                      tickFormatter={(d) => d.slice(5)}
                      interval={4}
                      axisLine={{ stroke: "#27272A" }}
                      tickLine={false}
                    />
                    <YAxis tick={{ fill: "#71717A", fontSize: 10 }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#0F0F11", border: "1px solid #27272A", borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: "#71717A" }}
                    />
                    <Area type="monotone" dataKey="count" name="Посещения" stroke="#00F0FF" strokeWidth={2} fill="url(#visitsGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <RankList
                icon={Globe2}
                title="Топ языков"
                items={stats.topLanguages}
                renderLabel={(i) => LANG_NAMES[i.language] || i.language}
                testId="admin-top-languages"
              />
              <RankList
                icon={MapPin}
                title="Топ стран"
                items={stats.topCountries}
                renderLabel={(i) => i.country}
                testId="admin-top-countries"
              />
              <RankList
                icon={MousePointerClick}
                title="Клики Google Play по источникам"
                items={(stats.googlePlayClickSources || []).map((s) => ({ source: s.source, count: s.count }))}
                renderLabel={(i) => i.source}
                testId="admin-google-play-click-sources"
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
